import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { usePublicClient, useReadContract, useWriteContract } from "wagmi";
import { ERC721DropAbi } from "@atelier/abis";
import { AllowlistManager } from "./AllowlistManager";
import { CollectionHeader } from "./CollectionHeader";
import { CollectionMetadataPanel } from "./CollectionMetadataPanel";
import { useChainMismatch } from "@/hooks/useChainMismatch";
import type { Collection } from "./types";

/**
 * Dashboard row for a Drop collection. Owner controls only — pause / withdraw
 * are direct on-chain actions, allowlist + reveal + contract metadata are
 * handled by their own sub-components to keep this component focused on
 * orchestration.
 */
export function DropRow({ c }: { c: Collection }) {
  const qc = useQueryClient();
  const { writeContractAsync, isPending } = useWriteContract();
  const pc = usePublicClient({ chainId: c.chainId });
  // Every on-chain action below targets `c.chainId` specifically — the
  // collection lives on that chain regardless of what the dashboard
  // chain filter is set to. Block + tell the user when the wallet's on
  // a different chain.
  const guard = useChainMismatch(c.chainId);

  const { data: revealed, refetch: refetchRevealed } = useReadContract({
    address: c.contractAddress,
    abi: ERC721DropAbi,
    functionName: "revealed",
    chainId: c.chainId,
  });

  // For PFP-derived blind-mint collections we know the canonical
  // metadata URL from the chain id + contract address (worker route),
  // so prefill it. Non-PFP drops keep the manual input.
  const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? "";
  const defaultRevealUri = c.pfpProjectId && apiBase
    ? `${apiBase.replace(/\/$/, "")}/metadata/${c.chainId}/${c.contractAddress.toLowerCase()}/`
    : "";
  const [revealUri, setRevealUri] = useState(defaultRevealUri);
  const [revealStatus, setRevealStatus] = useState<string | null>(null);
  const [showAllowlist, setShowAllowlist] = useState(false);
  const [showMeta, setShowMeta] = useState(false);

  const reveal = async () => {
    if (!revealUri) return;
    setRevealStatus("Confirm in wallet…");
    try {
      const hash = await writeContractAsync({
        address: c.contractAddress,
        abi: ERC721DropAbi,
        functionName: "reveal",
        args: [revealUri],
        chainId: c.chainId,
      });
      setRevealStatus("Confirming on-chain…");
      if (pc) await pc.waitForTransactionReceipt({ hash });
      setRevealStatus("Revealed.");
      setRevealUri("");
      await refetchRevealed();
    } catch (err) {
      setRevealStatus(`Error: ${(err as Error).message}`);
    }
  };

  const togglePause = async () => {
    try {
      const hash = await writeContractAsync({
        address: c.contractAddress,
        abi: ERC721DropAbi,
        functionName: "setPaused",
        args: [!c.isPaused],
        chainId: c.chainId,
      });
      if (pc) await pc.waitForTransactionReceipt({ hash });
      await qc.invalidateQueries({ queryKey: ["my-collections"] });
    } catch (err) {
      console.error(err);
    }
  };

  const withdraw = async () => {
    try {
      let recipient: `0x${string}` = c.contractAddress;
      if (window.ethereum) {
        const accts = (await window.ethereum.request({ method: "eth_accounts" })) as string[];
        if (accts[0]) recipient = accts[0] as `0x${string}`;
      }
      const hash = await writeContractAsync({
        address: c.contractAddress,
        abi: ERC721DropAbi,
        functionName: "withdraw",
        args: [recipient],
        chainId: c.chainId,
      });
      if (pc) await pc.waitForTransactionReceipt({ hash });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="card flex flex-col gap-3">
      <CollectionHeader c={c} />
      {guard.wrong && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-200">
          <span className="font-medium">Wallet is on a different chain.</span>
          <button
            className="rounded border border-amber-500 px-2 py-0.5 text-[11px] font-semibold hover:bg-amber-500 hover:text-amber-50 disabled:opacity-50"
            onClick={guard.switchToTarget}
            disabled={guard.isSwitching}
          >
            {guard.isSwitching ? "Switching…" : `Switch to ${guard.targetName}`}
          </button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          className="btn-ghost h-8 px-3 text-xs"
          disabled={isPending || guard.wrong}
          title={guard.tooltip}
          onClick={togglePause}
        >
          {c.isPaused ? "Unpause" : "Pause"}
        </button>
        <button
          className="btn-ghost h-8 px-3 text-xs"
          disabled={isPending || guard.wrong}
          title={guard.tooltip}
          onClick={withdraw}
        >
          Withdraw
        </button>
        <button
          className="btn-ghost h-8 px-3 text-xs"
          onClick={() => setShowAllowlist((v) => !v)}
          aria-expanded={showAllowlist}
        >
          {showAllowlist ? "Hide allowlist" : "Manage allowlist"}
        </button>
        <button
          className="btn-ghost h-8 px-3 text-xs"
          onClick={() => setShowMeta((v) => !v)}
          aria-expanded={showMeta}
        >
          {showMeta ? "Hide metadata" : "Royalty + contractURI"}
        </button>
      </div>
      {revealed === false && (
        <div className="flex flex-col gap-2 rounded-md border border-violet-300 bg-violet-50 p-3 text-xs dark:border-violet-700/40 dark:bg-violet-900/20">
          <span className="font-semibold text-violet-900 dark:text-violet-200">
            🎭 Pre-reveal — reveal the collection to swap from placeholder to real metadata.
          </span>
          <div className="flex flex-wrap gap-2">
            <input
              className="input h-8 flex-1 min-w-[220px] text-[11px]"
              placeholder="ipfs://Qm.../ (final baseURI, with trailing slash)"
              value={revealUri}
              onChange={(e) => setRevealUri(e.target.value)}
              disabled={isPending}
            />
            <button
              className="btn-primary h-8 px-3 text-xs"
              disabled={isPending || !revealUri || guard.wrong}
              title={guard.tooltip}
              onClick={() => void reveal()}
            >
              Reveal
            </button>
          </div>
          {revealStatus && <span className="text-muted-foreground">{revealStatus}</span>}
        </div>
      )}
      {showAllowlist && (
        <AllowlistManager contractAddress={c.contractAddress} chainId={c.chainId} />
      )}
      {showMeta && (
        <CollectionMetadataPanel
          contractAddress={c.contractAddress}
          chainId={c.chainId}
          kind="drop"
          name={c.name}
          description={c.description}
          imageCid={c.imageCid}
          bannerCid={c.bannerCid}
        />
      )}
    </div>
  );
}
